import { X, AlertTriangle } from "lucide-react";
import WorkDatesPicker from "@/components/WorkDatesPicker";
import ReliabilityPill from "@/components/ReliabilityPill";
import { useInviteForm } from "@/hooks/useInviteForm";
import ExtraToolsField from "@/components/ExtraToolsField";
import { FeeBreakdown } from "@/components/FeeBreakdown";
import TimeRangePicker from "@/components/TimeRangePicker";
import LunchNote from "@/components/LunchNote";
import AddressSearch from "@/components/AddressSearch";
import ConfirmDatesModal from "@/components/ConfirmDatesModal";

const INPUT = "w-full min-w-0 bg-black border border-white/15 focus:border-[#FF6A00] focus:outline-none px-3 py-3 text-white";
const LABEL = "form-label";
const NO_PREFILL = {};

function Field({ label, val, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className={LABEL}>{label}</label>
      <input value={val} onChange={onChange} type={type} placeholder={placeholder} className={INPUT} data-testid={testid} />
    </div>
  );
}

function LateCancelWarning({ count }) {
  if (!(count >= 2)) return null;
  return (
    <div className="mt-4 border border-red-500/50 bg-red-500/10 p-3 flex items-start gap-3" data-testid="invite-latecancel-warning">
      <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
      <div className="text-sm">
        <div className="font-bold uppercase tracking-widest text-[11px] text-red-400">Heads up — repeat late-canceller</div>
        <div className="text-red-200 mt-1">This tradie has <span className="font-bold">{count} late cancellation{count > 1 ? "s" : ""}</span> (within 24h of start) in the last 90 days. You can still book them, but the deposit is exposed if they no-show again.</div>
      </div>
    </div>
  );
}

function InviteFields({ form, flex = [] }) {
  const { f, setF, setK, setDates, setBilling, setTimes, hourly, trade } = form;
  return (
    <div className="mt-6 space-y-5" data-testid="invite-fields">
      <Field label="Job description (e.g. decking, wall framing etc.)" val={f.title} onChange={setK("title")} testid="invite-title" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2"><AddressSearch value={f.site_address || ""} onSelect={(a) => setF((s) => ({ ...s, site_address: a.full, site_lat: a.lat ?? null, site_lng: a.lng ?? null, city: a.suburb || s.city }))} onClear={() => setF((s) => ({ ...s, site_address: "", site_lat: null, site_lng: null, city: "" }))} testid="invite-address" /></div>
        {f.city && <div className="md:col-span-2 -mt-2 text-xs text-zinc-400">Suburb shown to the tradie up front: <span className="text-white font-bold">{f.city}</span></div>}
        <div className="md:col-span-2" data-testid="invite-dates">
          <WorkDatesPicker dates={f.work_dates || []} flexible={!!f.flexible} windowEnd={f.end_date || ""} durationDays={f.duration_days} onChange={setDates} single={hourly} testid="invite-work-dates" />
          {f.flexible && <div className="mt-3 max-w-xs"><Field label="How many days inside that window?" type="number" val={f.duration_days} onChange={setK("duration_days")} testid="invite-duration" /></div>}
        </div>
        {flex.length > 0 && (
          <div className="md:col-span-2 flex gap-1" data-testid="invite-billing-toggle">
            {[["daily", "Full day(s)"], ["hourly", `Just the hours (${flex[0].hours_available}h listed · min 4h)`]].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setBilling(k, flex[0])} className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border ${f.billing === k ? "border-[#FF6A00] bg-[#FF6A00] text-black" : "border-white/15 text-zinc-300 hover:border-[#FF6A00]"}`} data-testid={`invite-billing-${k}`}>{l}</button>
            ))}
          </div>
        )}
        <div className="md:col-span-2"><TimeRangePicker start={f.start_time} finish={f.finish_time} onChange={({ start, finish }) => setTimes(start, finish)} minHours={hourly ? 4 : 1} testid="invite-times" /></div>
        {hourly ? (
          <Field label="Hourly rate (AUD incl. GST)" type="number" val={f.hourly_rate} onChange={setK("hourly_rate")} testid="invite-hourly-rate" />
        ) : (
          <div><Field label={`Day rate the tradie receives (AUD incl. GST)${Number(f.duration_days) > 1 ? ` × ${f.duration_days} days` : ""}`} type="number" val={f.day_rate} onChange={setK("day_rate")} testid="invite-rate" /><LunchNote className="mt-2" testid="invite-lunch-note" /></div>
        )}
      </div>
      <div className={`border p-4 ${f.weatherproof ? "border-[#16A34A]/50 bg-[#16A34A]/5" : "border-white/10 bg-black"}`} data-testid="invite-weather-card">
        <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
          <input type="checkbox" checked={!!f.weatherproof} onChange={setK("weatherproof")} className="accent-[#16A34A]" data-testid="invite-weatherproof" />
          <span className="font-semibold">Not weather dependent</span><span className="text-zinc-500 text-xs">— indoor or under cover; this job goes ahead rain, hail or shine.</span>
        </label>
      </div>
      <div>
        <label className={LABEL}>More details (optional) — what to bring, site access, parking</label>
        <textarea rows={3} value={f.description} onChange={setK("description")} className={INPUT} data-testid="invite-desc" />
      </div>
      <ExtraToolsField trade={trade} value={f.extra_tools} onChange={(v) => setF((s) => ({ ...s, extra_tools: v }))} testid="invite-extra-tools" />
      <div>
        <label className={LABEL}>Note to the tradie (optional)</label>
        <textarea rows={2} value={f.note} onChange={setK("note")} className={INPUT} data-testid="invite-note" />
      </div>
    </div>
  );
}

export default function InviteModal({ open, onClose, contractor, prefill = NO_PREFILL, onCreated }) {
  const form = useInviteForm({ open, contractor, prefill, onCreated, onClose });
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/85 flex items-start justify-center z-50 p-4 overflow-auto" data-testid="invite-modal">
      <div className="max-w-3xl w-full border border-white/10 bg-[#121212] p-6 mt-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-mono-data text-xs tracking-widest text-[#FF6A00]">// SEND OFFER</div>
            <h2 className="font-display font-black text-3xl uppercase tracking-tighter">Book {contractor?.name}</h2>
            <p className="text-zinc-400 text-sm mt-1">Fill it out like a job post — they get a ping the moment you send.</p>
            {contractor?.reliability && <div className="mt-2"><ReliabilityPill reliability={contractor.reliability} testid="invite-reliability" /></div>}
          </div>
          <button onClick={onClose} className="w-8 h-8 flex-shrink-0 flex items-center justify-center border border-red-500/60 text-red-500 hover:bg-red-500 hover:text-black" data-testid="invite-modal-close"><X className="w-4 h-4" /></button>
        </div>
        <LateCancelWarning count={contractor?.late_cancel_90d} />
        <InviteFields form={form} flex={contractor?.flexible_availability || []} />
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-white/10 pt-4">
          <div>
            <FeeBreakdown amount={form.total} label={`${contractor?.name?.split(" ")[0] || "Tradie"} receives`} testid="invite-fee" />
            <div className="text-[10px] font-mono-data text-zinc-500 mt-1">Money safely held by Stripe until you sign off</div>
          </div>
          <div className="grid grid-cols-2 sm:flex gap-2">
            <button onClick={onClose} className="whitespace-nowrap px-4 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-widest hover:border-red-500 hover:text-red-500" data-testid="invite-cancel">Cancel</button>
            <button onClick={() => form.submit()} disabled={!form.canSubmit} className="whitespace-nowrap px-6 py-3.5 bg-[#FF6A00] text-black text-sm font-black uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-50" data-testid="invite-submit">
              {form.busy ? "Sending…" : "Send offer"}
            </button>
          </div>
        </div>
      </div>
      <ConfirmDatesModal open={form.confirmOpen} who={contractor?.name} terms={form.f} action="Send offer" busy={form.busy} onConfirm={() => form.submit(true)} onClose={() => form.setConfirmOpen(false)} />
    </div>
  );
}
